import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Edit2, Key, Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { clientApi, transformClientData } from '../../../api/clientApi';

interface Mod {
  id: string;
  name: string;
  email: string;
  status: 'active' | 'invited';
}

function mapRow(r: ReturnType<typeof transformClientData>[0]): Mod {
  return {
    id: String(r.id),
    name: r.name,
    email: r.email,
    status: r.isActive ? 'active' : 'invited',
  };
}

function createModeratorErrorMessage(e: unknown): string {
  const ax = e as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = ax.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((x: { msg?: string; loc?: unknown }) => x?.msg).filter(Boolean).join(', ') || 'Request failed';
  }
  return ax.message || 'Create failed';
}

export function ModeratorsPage() {
  const [rows, setRows] = useState<Mod[]>([]);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  /** Optional initial password for new moderator (API: min 8 chars when set). */
  const [invitePassword, setInvitePassword] = useState('');
  const [inviting, setInviting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* ── Edit modal ── */
  const [editTarget, setEditTarget] = useState<Mod | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [saving, setSaving] = useState(false);

  /* ── Reset password modal ── */
  const [resetTarget, setResetTarget] = useState<Mod | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await clientApi.getClients();
      const list = Array.isArray(raw) ? transformClientData(raw) : [];
      setRows(list.map(mapRow));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load moderators';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const toRemove = removeId ? rows.find((r) => r.id === removeId) : undefined;

  const confirmRemove = async () => {
    if (!removeId) return;
    try {
      await clientApi.deleteClient(removeId);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Delete failed';
      setError(msg);
      setRemoveId(null);
      return;
    }
    setRows((r) => r.filter((x) => x.id !== removeId));
    setRemoveId(null);
    await refresh();
  };

  const handleInvite = async (e?: FormEvent) => {
    e?.preventDefault();
    const name = inviteName.trim();
    const email = inviteEmail.trim().toLowerCase();
    const pwd = invitePassword.trim();
    if (!name || !email) {
      toast.error('Enter name and email');
      return;
    }
    if (pwd && pwd.length < 8) {
      toast.error('Initial password must be at least 8 characters (or leave blank)');
      return;
    }
    setError(null);
    setInviting(true);
    try {
      await clientApi.createClient(
        pwd ? { name, email, password: pwd } : { name, email },
      );
      toast.success('Moderator created');
      setInviteName('');
      setInviteEmail('');
      setInvitePassword('');
      await refresh();
    } catch (err: unknown) {
      const msg = createModeratorErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setInviting(false);
    }
  };

  const openEdit = (mod: Mod) => {
    setEditTarget(mod);
    setEditName(mod.name);
    setEditEmail(mod.email);
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await clientApi.updateClient(editTarget.id, { name: editName.trim(), email: editEmail.trim() });
      toast.success('Moderator updated');
      setEditTarget(null);
      await refresh();
    } catch {
      toast.error('Failed to update moderator');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget || !newPassword.trim()) return;
    setResetting(true);
    try {
      await clientApi.setPassword(resetTarget.id, newPassword.trim());
      toast.success('Password updated');
      setResetTarget(null);
      setNewPassword('');
    } catch {
      toast.error('Failed to update password');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="hawk-admin-surface flex min-h-0 flex-1 flex-col overflow-hidden font-[Inter,ui-sans-serif,system-ui,sans-serif]">
      <header className="shrink-0">
        <div className="px-3 py-3 sm:px-4 sm:py-3.5 md:px-5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/50 bg-white/40 text-[#1d4ed8] shadow-[0_8px_24px_rgba(15,23,42,0.06)] backdrop-blur-xl ring-1 ring-indigo-200/30">
              <UserPlus className="h-5 w-5" strokeWidth={2.1} />
            </span>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight text-[#0f172a] sm:text-3xl">
                Moderators
              </h1>
              <p className="mt-0.5 text-sm leading-tight text-slate-500">
                Invite and manage editorial access
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain px-3 pb-4 sm:gap-3.5 sm:px-4 md:px-5">
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}

        <form
          className="flex min-w-0 flex-col gap-3 rounded-2xl border border-white/50 bg-white/40 p-4 shadow-[0_8px_32px_rgba(15,23,42,0.08)] backdrop-blur-xl backdrop-saturate-150 ring-1 ring-indigo-200/35 sm:flex-row sm:flex-wrap sm:items-end sm:p-5"
          onSubmit={handleInvite}
        >
          <div className="min-w-0 space-y-1.5 sm:w-44">
            <Label htmlFor="invite-name" className="text-xs font-medium text-slate-600">
              Name
            </Label>
            <Input
              id="invite-name"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder="Team member name"
              className="w-full rounded-xl border border-white/60 bg-white/90"
              autoComplete="name"
            />
          </div>
          <div className="min-w-0 space-y-1.5 sm:w-56">
            <Label htmlFor="invite-email" className="text-xs font-medium text-slate-600">
              Email
            </Label>
            <Input
              id="invite-email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="moderator@company.com"
              className="w-full rounded-xl border border-white/60 bg-white/90"
              autoComplete="email"
            />
          </div>
          <div className="min-w-0 space-y-1.5 sm:w-40">
            <Label htmlFor="invite-password" className="text-xs font-medium text-slate-600">
              Initial password <span className="font-normal text-slate-400">(optional)</span>
            </Label>
            <Input
              id="invite-password"
              type="password"
              value={invitePassword}
              onChange={(e) => setInvitePassword(e.target.value)}
              placeholder="Min 8 chars if set"
              className="w-full rounded-xl border border-white/60 bg-white/90"
              autoComplete="new-password"
            />
          </div>
          <Button
            type="submit"
            disabled={inviting}
            className="h-10 gap-2 rounded-xl border-0 bg-[#1d4ed8] text-white shadow-sm hover:bg-[#1e3a8a]"
          >
            {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Add moderator
          </Button>
        </form>

        <Card className="min-h-0 flex-1 gap-0 overflow-hidden rounded-2xl border border-white/50 !bg-white/40 shadow-[0_8px_32px_rgba(15,23,42,0.08)] backdrop-blur-xl backdrop-saturate-150 ring-1 ring-indigo-200/35">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin text-[#0284c7]" />
                Loading…
              </div>
            ) : rows.length === 0 ? (
              <p className="px-6 py-12 text-center text-sm text-slate-400">No moderators yet.</p>
            ) : (
              <div className="hawk-scrollbar-rainbow max-h-[min(36rem,calc(100vh-16rem))] overflow-auto">
                <table className="w-full min-w-[40rem] text-sm">
                  <thead className="sticky top-0 z-10 border-b border-white/40 bg-white/55 backdrop-blur-md">
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">
                      <th className="px-6 py-3">Name</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="w-44 px-6 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/40">
                    {rows.map((m) => (
                      <tr key={m.id} className="transition-colors hover:bg-white/40">
                        <td className="px-6 py-3.5 font-semibold text-[#0f172a]">{m.name}</td>
                        <td className="px-4 py-3.5 text-slate-600">{m.email}</td>
                        <td className="px-4 py-3.5">
                          <Badge
                            variant="outline"
                            className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize ${
                              m.status === 'active'
                                ? 'border-emerald-200/90 bg-emerald-50/90 text-[#059669]'
                                : 'border-white/60 bg-white/50 text-slate-600'
                            }`}
                          >
                            {m.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-3.5">
                          <div className="flex items-center justify-end gap-0.5">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 rounded-lg px-2 text-slate-600 hover:bg-white/50 hover:text-sky-700"
                              onClick={() => openEdit(m)}
                              title="Edit"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 rounded-lg px-2 text-slate-600 hover:bg-white/50 hover:text-amber-700"
                              onClick={() => {
                                setResetTarget(m);
                                setNewPassword('');
                              }}
                              title="Reset password"
                            >
                              <Key className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 rounded-lg px-2 text-red-600 hover:bg-white/50"
                              onClick={() => setRemoveId(m.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete confirm */}
      <AlertDialog open={!!removeId} onOpenChange={(open) => !open && setRemoveId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove moderator?</AlertDialogTitle>
            <AlertDialogDescription>
              {toRemove ? `${toRemove.name} will lose access.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              type="button"
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={confirmRemove}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit modal — pearl glass */}
      <AlertDialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <AlertDialogContent className="gap-5 rounded-[1.25rem] border border-sky-100/90 !bg-[linear-gradient(165deg,rgba(255,255,255,0.88)_0%,rgba(239,246,255,0.72)_100%)] p-6 shadow-[0_24px_60px_-20px_rgba(14,165,233,0.28),0_1px_0_rgba(255,255,255,0.85)_inset] backdrop-blur-xl backdrop-saturate-150 sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-left text-lg font-bold tracking-tight text-[#0f172a]">
              Edit moderator
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-4 py-0.5">
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium text-slate-500">Name</Label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="h-10 rounded-xl border border-sky-100/90 bg-white/85 text-[#0f172a] shadow-none focus-visible:border-[#1d4ed8]/35 focus-visible:ring-[#1d4ed8]/15"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium text-slate-500">Email</Label>
              <Input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                className="h-10 rounded-xl border border-sky-100/90 bg-white/85 text-[#0f172a] shadow-none focus-visible:border-[#1d4ed8]/35 focus-visible:ring-[#1d4ed8]/15"
              />
            </div>
          </div>
          <AlertDialogFooter className="gap-2 sm:gap-2.5">
            <AlertDialogCancel
              type="button"
              className="h-10 rounded-xl border border-sky-100/90 bg-white/80 px-4 font-medium text-[#0f172a] shadow-none hover:bg-white"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              type="button"
              onClick={handleSaveEdit}
              disabled={saving || !editName.trim()}
              className="h-10 rounded-xl border-0 bg-gradient-to-b from-[#3b82f6] to-[#1d4ed8] px-5 font-semibold text-white shadow-[0_8px_20px_rgba(29,78,216,0.35)] hover:brightness-105"
            >
              {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset password modal — pearl glass */}
      <AlertDialog open={!!resetTarget} onOpenChange={(open) => !open && setResetTarget(null)}>
        <AlertDialogContent className="gap-5 rounded-[1.25rem] border border-sky-100/90 !bg-[linear-gradient(165deg,rgba(255,255,255,0.88)_0%,rgba(239,246,255,0.72)_100%)] p-6 shadow-[0_24px_60px_-20px_rgba(14,165,233,0.28),0_1px_0_rgba(255,255,255,0.85)_inset] backdrop-blur-xl backdrop-saturate-150 sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-left text-lg font-bold tracking-tight text-[#0f172a]">
              Reset password
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left text-sm text-slate-500">
              Set a new password for <strong className="font-semibold text-[#0f172a]">{resetTarget?.name}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5 py-0.5">
            <Label className="text-[13px] font-medium text-slate-500">New password</Label>
            <Input
              type="password"
              className="h-10 rounded-xl border border-sky-100/90 bg-white/85 text-[#0f172a] shadow-none focus-visible:border-[#1d4ed8]/35 focus-visible:ring-[#1d4ed8]/15"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          <AlertDialogFooter className="gap-2 sm:gap-2.5">
            <AlertDialogCancel
              type="button"
              className="h-10 rounded-xl border border-sky-100/90 bg-white/80 px-4 font-medium text-[#0f172a] shadow-none hover:bg-white"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              type="button"
              onClick={handleResetPassword}
              disabled={resetting || !newPassword.trim()}
              className="h-10 rounded-xl border-0 bg-gradient-to-b from-[#3b82f6] to-[#1d4ed8] px-5 font-semibold text-white shadow-[0_8px_20px_rgba(29,78,216,0.35)] hover:brightness-105"
            >
              {resetting && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              Set password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
